import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { Options } from "@/modules/common/types";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { createOptions } from "@/modules/common/utils";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import { CommissionTypes, UnitIdTypes } from "@/modules/lead-page/types";
import { SaleTypeNames, SalesStatuses } from "@/modules/sales/constants";
import { isSameStatus } from "@/modules/lead-page/utils/status-name";

const dateSchema = z.date({
  required_error: "Required",
  invalid_type_error: "Invalid date",
});

const usePromiseSaleForm = ({
  handleSaleSubmission,
  existingSale,
  selectedUnitLocation,
  statusName,
}: {
  handleSaleSubmission: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => void;
  existingSale?: any;
  selectedUnitLocation: Options | undefined;
  statusName?: string;
}) => {
  const { selectedOrganization } = useOrganization();
  const { lead } = useContext(LeadPageContext);

  // Property Track is only shown/required for resale sellers, not resale buyers.
  const isResaleSeller = (lead?.data?.lead_type?.lead_type || "")
    .toLowerCase()
    .includes("seller");

  const { PropertyTypeData } = useLocationsData();

  const [propertyTypeOptions, setPropertyTypeOptions] = useState<any[]>([]);

  const promiseSaleFormSchema = z
    .object({
      promiseSaleDate: dateSchema,
      compoundUId: z.string().min(1, "Required"),
      unitValue: z.string().transform((val, ctx) => {
        const parsed = parseFloat(val);
        if (isNaN(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Not a number",
          });
          return z.NEVER;
        }
        return parsed;
      }),
      unitNumber: z.string().min(1, "Required"),
      propertyTrackUId: isResaleSeller
        ? z.string().min(1, "Required")
        : z.string().optional(),
      unitIdType: z.enum([UnitIdTypes.Nawy, UnitIdTypes.Outside], {
        required_error: "Required",
      }),
      nawyUnitId: z.string().min(1, "Required"),
      propertyTypeUId: z.string().min(1, "Required"),
      nawyCommissionType: z.enum(
        [CommissionTypes.Percentage, CommissionTypes.Fixed],
        {
          required_error: "Required",
        }
      ),
      nawyCommissionValue: z.string().transform((val, ctx) => {
        const parsed = parseFloat(val);
        if (isNaN(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Not a number",
          });
          return z.NEVER;
        }
        if (parsed < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be a positive number",
          });
          return z.NEVER;
        }
        return parsed;
      }),
      commissionCollectedValue: z
        .string()
        .optional()
        .transform((val, ctx) => {
          if (!val || val === "") return undefined;
          const parsed = parseFloat(val);
          if (isNaN(parsed)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Not a number",
            });
            return z.NEVER;
          }
          if (parsed < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Must be a positive number",
            });
            return z.NEVER;
          }
          return parsed;
        }),
      dateOfContraction: isSameStatus(
        statusName,
        SalesStatuses.CONGRATS_ITS_A_SALE,
      )
        ? dateSchema
        : z.date().optional(),
      expectedContractionDate: isSameStatus(
        statusName,
        SalesStatuses.CONGRATS_ITS_A_SALE,
      )
        ? z.date().optional()
        : dateSchema,
      saleCategory: z.string().min(1, "Required"),
      nameOnContract: z.string().min(1, "Required"),
      areaUId: z.string().optional(),
      organizationUId: z.string().optional(),
      comment: z.string().min(4, "Minimum 4 characters"),
    })
    .refine(
      (data) => {
        // Only validate if both dates are present
        if (!data.promiseSaleDate || !data.expectedContractionDate) {
          return true;
        }

        // Compare dates by setting time to same value to avoid time zone issues
        const promiseDate = new Date(data.promiseSaleDate);
        const expectedDate = new Date(data.expectedContractionDate);

        // Set both to start of day for accurate comparison
        promiseDate.setHours(0, 0, 0, 0);
        expectedDate.setHours(0, 0, 0, 0);

        return promiseDate <= expectedDate;
      },
      {
        message:
          "Promise sale date cannot be after the expected contracting date",
        path: ["promiseSaleDate"],
      }
    )
    .refine(
      (data) => {
        if (data.nawyCommissionType === CommissionTypes.Percentage) {
          return data.nawyCommissionValue <= 100;
        }
        return true;
      },
      {
        message: "Percentage cannot exceed 100%",
        path: ["nawyCommissionValue"],
      }
    );

  type PromiseSaleFormData = z.infer<typeof promiseSaleFormSchema>;

  const formDefaultValues = useMemo(() => {
    return {
      promiseSaleDate: existingSale?.promiseSaleDate
        ? new Date(existingSale.promiseSaleDate)
        : undefined,
      compoundUId: existingSale?.compound?.uid ?? undefined,
      unitValue: existingSale?.unitValue?.toString() ?? undefined,
      unitNumber: existingSale?.unitNumber ?? "",
      propertyTrackUId: existingSale?.propertyTrackUid ?? "",
      unitIdType: existingSale?.unitIdType ?? undefined,
      nawyUnitId: existingSale?.nawyUnitId ?? "",
      propertyTypeUId: existingSale?.propertyType?.uid ?? undefined,
      nawyCommissionType: existingSale?.nawyCommissionType ?? undefined,
      nawyCommissionValue:
        existingSale?.nawyCommissionValue?.toString() ?? undefined,
      commissionCollectedValue:
        existingSale?.commissionCollectedValue?.toString() ?? undefined,
      dateOfContraction: new Date(),
      expectedContractionDate: existingSale?.expectedContractionDate
        ? new Date(existingSale.expectedContractionDate)
        : undefined,
      saleCategory: existingSale?.saleCategory ?? "",
      nameOnContract: existingSale?.nameOnContract ?? "",
      areaUId: existingSale?.area?.uid ?? undefined,
      organizationUId: existingSale?.organization?.uid ?? undefined,
      comment: existingSale?.comment ?? "",
    };
  }, [existingSale]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<PromiseSaleFormData>({
    resolver: zodResolver(promiseSaleFormSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: formDefaultValues,
  });

  const selectedCompound = watch("compoundUId");
  const nawyCommissionType = watch("nawyCommissionType");

  useEffect(() => {
    setPropertyTypeOptions(
      createOptions({
        list: PropertyTypeData,
        field1: "uid",
        field2: "name",
      })
    );
  }, [PropertyTypeData]);

  useEffect(() => {
    if (existingSale) {
      const areaUId = existingSale?.area?.uid;
      const compoundUId = existingSale?.compound?.uid;

      reset({
        promiseSaleDate: existingSale.promiseSaleDate
          ? new Date(existingSale.promiseSaleDate)
          : undefined,
        compoundUId: compoundUId,
        unitValue: existingSale.unitValue?.toString() || "",
        unitNumber: existingSale.unitNumber || "",
        propertyTrackUId: existingSale.propertyTrackUid || "",
        unitIdType: existingSale.unitIdType || undefined,
        nawyUnitId: existingSale.nawyUnitId || "",
        propertyTypeUId: existingSale?.propertyType?.uid || undefined,
        nawyCommissionType: existingSale.nawyCommissionType || undefined,
        nawyCommissionValue: existingSale.nawyCommissionValue?.toString() || "",
        commissionCollectedValue:
          existingSale.commissionCollectedValue?.toString() || undefined,
        dateOfContraction: new Date(),
        expectedContractionDate: existingSale.expectedContractionDate
          ? new Date(existingSale.expectedContractionDate)
          : undefined,
        saleCategory: existingSale.saleCategory || "",
        nameOnContract: existingSale.nameOnContract || "",
        areaUId: areaUId,
        organizationUId: lead?.data?.lead?.organization?.uid,
        comment: existingSale.comment || "",
      });

      setValue("areaUId", areaUId);
      setValue("compoundUId", compoundUId);
    }
  }, [existingSale, reset, setValue, lead?.data?.lead?.organization?.uid]);

  const formatDate = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    return date.toISOString().split("T")[0];
  };

  const onSubmit = (data: PromiseSaleFormData) => {
    const areaUId =
      existingSale?.area?.uid || selectedUnitLocation?.optionValueUid;

    const formattedData = {
      compoundUId: data.compoundUId,
      promiseSaleDate: formatDate(data.promiseSaleDate),
      unitValue: data.unitValue,
      unitNumber: data.unitNumber,
      unitIdType: data.unitIdType,
      nawyUnitId: data.nawyUnitId,
      propertyTypeUId: data.propertyTypeUId,
      nawyCommissionType: data.nawyCommissionType,
      nawyCommissionValue: data.nawyCommissionValue,
      ...(data.commissionCollectedValue && {
        commissionCollectedValue: data.commissionCollectedValue,
      }),
      dateOfContraction: formatDate(data.dateOfContraction),
      saleCategory: data.saleCategory,
      nameOnContract: data.nameOnContract,
      saleTypeName: isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE)
        ? SaleTypeNames.Contracted
        : SaleTypeNames.Promise,
      ...(selectedUnitLocation && { unitLocation: selectedUnitLocation.label }),
      ...(areaUId && { areaUId: areaUId }),
      ...(!existingSale && { journeyUId: lead?.data?.lead?.journey_uid }),
      ...(!existingSale && { userUId: lead?.data?.lead?.user?.id }),
      ...(!existingSale && {
        organizationUId:
          lead?.data?.lead?.organization?.uid || selectedOrganization?.uid,
      }),
      comment: data.comment,
    };

    // Only include expectedContractionDate if it has a value
    if (data.expectedContractionDate) {
      formattedData.expectedContractionDate = formatDate(
        data.expectedContractionDate
      );
    }

    if (existingSale) {
      handleSaleSubmission(formattedData, "sale", {
        saleUId: existingSale?.uid,
        leadUId: existingSale?.lead?.uid,
      });
    } else {
      handleSaleSubmission(formattedData, "sale");
    }
  };

  return {
    control,
    handleSubmit,
    errors,
    onSubmit,
    selectedCompound,
    propertyTypeOptions,
    nawyCommissionType,
    setValue,
    watch,
  };
};

export default usePromiseSaleForm;
